import { BehaviorSubject } from 'rxjs';
import OrganizationsStore from './OrganizationsStore';
import { UserRoleSchema } from '../../definitions/schemas';
import { UserRoleStatus } from '../../enums/UserRoleStatus';

class UserRolesStore {
  private static instance: UserRolesStore;

  private currentRoleSubject: BehaviorSubject<UserRoleSchema | undefined>;

  private rolesSubject: BehaviorSubject<UserRoleSchema[]>;

  private constructor() {
    this.currentRoleSubject = new BehaviorSubject<UserRoleSchema | undefined>(
      undefined
    );
    this.rolesSubject = new BehaviorSubject<UserRoleSchema[]>([]);
  }

  public add(userRole: UserRoleSchema) {
    if (!userRole.organization.id) throw new Error('Unknown organization id');
    const knownRole = this.getRoleByOrganizationId(userRole.organization.id);
    if (!knownRole) {
      const userRoles = this.get();
      userRoles.push(userRole);
      this.set(userRoles);
    }
  }

  public clear() {
    this.rolesSubject.next([]);
  }

  public get() {
    return this.rolesSubject.getValue();
  }

  public getCurrent() {
    return this.currentRoleSubject.getValue();
  }

  public getCurrentObservable() {
    return this.currentRoleSubject.asObservable();
  }

  public getObservable() {
    return this.rolesSubject.asObservable();
  }

  // @todo load organizationId from OrganizationsStore
  public getRoleByOrganizationId(organizationId: string) {
    return this.get().find(
      (userRole: UserRoleSchema) => userRole.organization.id === organizationId
    );
  }

  public hasAnyRole() {
    return this.get().length > 0;
  }

  public set(userRoles: UserRoleSchema[]) {
    this.rolesSubject.next(userRoles);
  }

  public setExtended(userRoles: UserRoleSchema[]) {
    this.set(userRoles);
    // filter out inactive user roles in the organizations list
    const activeRoles = userRoles.filter(
      (userRole: UserRoleSchema) => userRole.status !== UserRoleStatus.Inactive
    );

    const organizations = activeRoles.map(
      (userRole: UserRoleSchema) => userRole.organization
    );

    OrganizationsStore.set(organizations);

    /**
     * For scenarios where users are accessing the app from a Mindtickle's
     * Learning Site, the auth token is a 1:1 user-organization relationship,
     * therefore, it can be assumed the organization obtained from the user
     * role (coming from the `me` request) is the current one. The same applies
     * for Standalone, if the user belongs to only one organization.
     */
    if (organizations.length === 1) {
      const currentOrg = organizations[0].id;
      OrganizationsStore.setCurrent(currentOrg);

      const userRole = this.getRoleByOrganizationId(currentOrg);
      this.setCurrent(userRole!);
    }
  }

  public setCurrent(userRole: UserRoleSchema) {
    this.currentRoleSubject.next(userRole);
  }

  public static getInstance(): UserRolesStore {
    if (!UserRolesStore.instance) {
      UserRolesStore.instance = new UserRolesStore();
    }
    return UserRolesStore.instance;
  }
}

// Export a singleton instance
const instance = UserRolesStore.getInstance();
Object.freeze(instance);
export default instance;
